// ---------------------------------------------------------------------------
// useTemplateVersions — fetch the immutable version snapshots for a single
// evaluation template. Stays idle (empty, non-loading) until both
// `workspaceName` and `templateName` are non-null. Returns versions in the
// order the server provides (ascending). Built on the shared `useAsyncData`
// primitive for race-safe loading + refresh.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { EvaluationTemplatesService } from '../../services/evaluationTemplatesService.js';
import type { EvaluationTemplateVersion } from '../../types/evaluationTemplate.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type {
  UseTemplateVersionsParams,
  UseTemplateVersionsResult,
} from './useTemplateVersions.interface.js';

export function useTemplateVersions(
  params: UseTemplateVersionsParams,
): UseTemplateVersionsResult {
  const { workspaceName, templateName } = params;
  const client = useHttpClient();
  const service = useMemo(
    () => new EvaluationTemplatesService(client),
    [client],
  );

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<EvaluationTemplateVersion[]>) | null
  >(() => {
    if (workspaceName === null || templateName === null) return null;
    return () => service.listVersions(workspaceName, templateName);
  }, [service, workspaceName, templateName]);

  const { data, isLoading, error, refresh } = useAsyncData<
    EvaluationTemplateVersion[]
  >(fetcher, { errorMessage: 'Failed to load versions' });

  return useMemo<UseTemplateVersionsResult>(
    () => ({ versions: data ?? [], isLoading, error, refresh }),
    [data, isLoading, error, refresh],
  );
}
